import { Component, Inject, Input, AfterViewInit } from 'ng-metadata/core';
import * as angular from 'angular';
import './back-to-top.scss';


/**
 * @ngdoc directive
 * @name mwContentStreamBackToTop
 * @module mw.components.contentStream
 * @param {boolean} [rely-on-sticky] - This flag determines if this button only shows when the sticky header is in play
 * @param {string} [mw-scroll-container-id] - ID of the container of which the content stream lives in
 * @param {number} [mw-show-offset] - number in pixels in which the back to top button will start to show. Will generally always be in sync with the sticky header
 * @param {string} [scrollToElementSelector] - This is the selector for determining the top for the content stream when there are other contents above it in the scrollable container
 * @description
 * `<mw-content-stream-back-to-top>` is a component that is responsible for bringing the content-streams scroll position back to the top of the page
 *  This is built to support our sticky-header component as well as not. If you plan to use the sticky-header functionality please ensure that you are using the `mw-sticky-header` directive
 *  on your `<mw-content-stream-header>` element
 * @usage
 * <hljs hljs-language="html">
 *  <mw-content-stream-back-to-top rely-on-sticky="true" mw-scroll-container-id="Some amazing ID!"> </mw-content-stream-back-to-top>
 * </hljs>
  */
@Component({
    selector: 'mw-content-stream-back-to-top',
    template: `
        <div class="mw-back-to-top" flex="auto" layout="row" layout-align="center">
            <md-button id="back-to-top" layout="row" ng-if="$ctrl.isVisible" class="mw-button md-primary mw-back-to-top-button md-raised" ng-class="{'mw-back-to-top-button-sticky': $ctrl.relyOnSticky, 'mw-back-to-top-button-non-sticky': !$ctrl.relyOnSticky}" ng-click="$ctrl.backToTop($event)" aria-label="{{'mw-content-stream-back-to-top__text' | translate}}">
                <md-icon md-svg-icon="navigation:ic_arrow_upward_24px"></md-icon>
                <span translate="mw-content-stream-back-to-top__text"></span>
            </md-button>
        </div>
        `
})

export class ContentStreamBackToTopComponent implements AfterViewInit {
    @Input('<') relyOnSticky: boolean = true;
    @Input('<') mwScrollContainerId: string;
    @Input('<') mwShowOffset: number = 1000;
    @Input('<') scrollToElementSelector: string;

    scrollContainerElement: ng.IAugmentedJQuery;
    scrollToElement: ng.IAugmentedJQuery;
    headerElement: Element;

    isVisible: boolean;
    isGoingUp: boolean;

    lastScrollPosition: number;

    constructor(
        @Inject('$scope') private $scope: ng.IScope,
        @Inject('$window') private $window: ng.IWindowService,
        @Inject('$element') private $element: ng.IAugmentedJQuery,
        @Inject('$mdUtil') private $mdUtil,
        @Inject('$timeout') private $timeout: ng.ITimeoutService
    ) {
        'ngInject';
    }

    ngAfterViewInit() {
        this.$timeout(() => {
            if (this.mwScrollContainerId) {
                this.scrollContainerElement = angular.element(this.$window.document.getElementById(this.mwScrollContainerId));
            } else {
                this.scrollContainerElement = angular.element(this.$element[0].closest('md-content'));
            }

            this.headerElement = this.$window.document.getElementsByTagName('mw-content-stream-header')[0];
            this.scrollToElement = angular.element(this.$window.document.querySelector(this.scrollToElementSelector));
            if (this.scrollContainerElement[0]) this.scrollContainerElement[0].addEventListener('scroll', this.scrollHandler.bind(this));
        });
    }

    scrollHandler() {
        if (this.scrollContainerElement[0].scrollTop > this.mwShowOffset && !this.isGoingUp) {
            if (this.headerElement.className.indexOf('mw-sticky-header--hide') !== -1) {
                setTimeout(() => this.isVisible = true, 250);
            } else if (this.headerElement.className.indexOf('mw-sticky-header--hide') === -1 && this.lastScrollPosition > this.scrollContainerElement[0].scrollTop) {
                this.isVisible = true;
            }
        } else if (this.scrollContainerElement[0].scrollTop <= this.headerElement.clientHeight || this.isGoingUp) {
            this.isVisible = false;
        }

        this.lastScrollPosition = this.scrollContainerElement[0].scrollTop;
        this.$scope.$evalAsync();
    }

    backToTop(event) {
        this.isVisible = false;
        this.isGoingUp = true;
        const scrollEnd = this.scrollToElement[0] ? this.scrollToElement[0].offsetTop : 0;
        this.$mdUtil.animateScrollTo(this.scrollContainerElement[0], scrollEnd, 1000);

        this.$timeout(() => {
            this.isGoingUp = false;
            this.$scope.$evalAsync();
        }, 1000);
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/content-stream/back-to-top/back-to-top.component.ts